import DialogContext from "components/confirm-dialog-context";
import { useSnackbarCustom } from "hooks/useSnackCustom";
import { IConsumidor } from "model/cliente.model";
import ConsignadoService from "pages/consignado/consignado.service";
import { FlowConsignadoListResponseDTO } from "pages/consignado/list/consignado-list.model";
import FinancasService from "pages/financas/financas.service";
import { useCallback, useContext, useRef } from "react";
import {
    setBloqueiaVendaConsignadoEmAndamento,
    setBloqueiaVendaContaReceberVencida,
    setVerificandoAlertaPoliticaVenda,
} from "redux/features/stateSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import ConsignadoEmAndamentoDialog from "./ConsignadoEmAndamentoDialog";
import { buildFiltroConsignadoEmAndamento, buildFiltroContaReceberVencida } from "./politica-venda-alerta.util";

const usePoliticaVendaAlerta = () => {
    const dispatch = useAppDispatch();
    const dialog = useContext(DialogContext);
    const { addError } = useSnackbarCustom();
    const financasService = FinancasService();
    const consignadoService = ConsignadoService();
    const configuracaoPoliticaVenda = useAppSelector(s => s.configuracoes.configuracaoPoliticaVenda);
    const estabelecimentoSelecionado = useAppSelector(s => s.estabelecimentoSelecionado);

    const ultimoClienteVerificadoRef = useRef<string | null>(null);

    const verificarAlertaPoliticaVenda = useCallback((cliente: IConsumidor) => {
        dispatch(setBloqueiaVendaContaReceberVencida(false));
        dispatch(setBloqueiaVendaConsignadoEmAndamento(false));

        const config = configuracaoPoliticaVenda;
        const deveVerificarContaReceber = !!config?.alertaContaReceberVencida || !!config?.bloqueiaVendaContaReceberVencida;
        const deveVerificarConsignado = !!config?.alertaConsignadoEmAndamento || !!config?.bloqueiaVendaConsignadoEmAndamento;

        if (!deveVerificarContaReceber && !deveVerificarConsignado) {
            return;
        }

        ultimoClienteVerificadoRef.current = cliente.uuid;
        dispatch(setVerificandoAlertaPoliticaVenda(true));

        const checkContaReceber: Promise<number | null> = deveVerificarContaReceber
            ? financasService.countContasReceber(buildFiltroContaReceberVencida(cliente, estabelecimentoSelecionado))
                .then(({ data }) => data)
                .catch(() => {
                    addError({
                        id: 'erro-verificar-conta-receber-politica-venda',
                        message: 'Não foi possível validar as contas a receber do cliente selecionado.',
                        closeable: true,
                        preventDuplicate: true,
                    });
                    return null;
                })
            : Promise.resolve(null);

        const checkConsignado: Promise<FlowConsignadoListResponseDTO[] | null> = deveVerificarConsignado
            ? consignadoService.list(buildFiltroConsignadoEmAndamento(cliente.uuid))
                .then(({ data }) => data.itens)
                .catch(() => {
                    addError({
                        id: 'erro-verificar-consignado-politica-venda',
                        message: 'Não foi possível validar o consignado do cliente selecionado.',
                        closeable: true,
                        preventDuplicate: true,
                    });
                    return null;
                })
            : Promise.resolve(null);

        Promise.all([checkContaReceber, checkConsignado]).then(([qtdContaReceberVencida, consignadosEmAndamento]) => {
            if (ultimoClienteVerificadoRef.current !== cliente.uuid) {
                return;
            }

            const possuiContaReceberVencida = !!qtdContaReceberVencida && qtdContaReceberVencida > 0;
            const possuiConsignadoEmAndamento = !!consignadosEmAndamento && consignadosEmAndamento.length > 0;

            if (possuiContaReceberVencida && config?.bloqueiaVendaContaReceberVencida) {
                dispatch(setBloqueiaVendaContaReceberVencida(true));
            }
            if (possuiConsignadoEmAndamento && config?.bloqueiaVendaConsignadoEmAndamento) {
                dispatch(setBloqueiaVendaConsignadoEmAndamento(true));
            }

            const abrirConsignadoSeAplicavel = () => {
                if (possuiConsignadoEmAndamento) {
                    dialog.confirm({
                        title: 'Atenção',
                        body: <ConsignadoEmAndamentoDialog itens={consignadosEmAndamento!} />,
                        error: true,
                        disableBackdropClose: true,
                        onConfirm: () => { },
                    });
                }
            };

            if (possuiContaReceberVencida) {
                dialog.confirm({
                    title: 'Atenção',
                    message: 'Cliente possui contas a receber vencidas.',
                    error: true,
                    disableBackdropClose: true,
                    onConfirm: abrirConsignadoSeAplicavel,
                });
            } else {
                abrirConsignadoSeAplicavel();
            }
        }).finally(() => {
            if (ultimoClienteVerificadoRef.current === cliente.uuid) {
                dispatch(setVerificandoAlertaPoliticaVenda(false));
            }
        });
        //eslint-disable-next-line
    }, [configuracaoPoliticaVenda, estabelecimentoSelecionado]);

    return {
        verificarAlertaPoliticaVenda,
    };
};

export default usePoliticaVendaAlerta;
